import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  ShoppingCartOutlined,
  WarningFilled,
  ZoomInOutlined,
} from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { Button, Image, Modal, Tag, theme } from 'antd';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { LgBadge } from '../../components/LgBadge';
import { ParamNameTitle } from '../../components/ParamNameTitle.tsx';
import { TestTubeColor } from '../../components/TestTubeColor';
import { BillingType, type ParameterSelection_RequestableParameterFragment } from '../../graphql/generated/graphql.ts';
import { deCollator } from '../../utils/collator';
import { currency } from '../../utils/currency';
import { humanizedHoursShort } from '../../utils/dateFormatUtils';
import { billingTypeTagLabelsFull, testTubeColorProperties } from '../../utils/enumHelpers';
import { openExternalLink } from '../../utils/linkOpener';

const { useToken } = theme;

/** "Größe (cm, Pflicht)" - tells the user upfront what they will have to fill in per Lokalisation. */
const describeLocalizationField = (field: { label: string; unit?: string | null; mandatory: boolean }) => {
  const notes = [field.unit, field.mandatory ? 'Pflicht' : null].filter(Boolean);
  return notes.length ? `${field.label} (${notes.join(', ')})` : field.label;
};

const useStyles = () => {
  const { token } = useToken();
  return useMemo(
    () => ({
      header: css`
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: ${token.paddingXS}px;
      `,
      name: css`
        font-size: ${token.fontSizeLG}px;
        font-weight: 600;
      `,
      nameSecondary: css`
        font-size: ${token.fontSize}px;
      `,
      pill: css`
        display: inline-flex;
        align-items: center;
        gap: 5px;
        height: 20px;
        padding: 0 ${token.paddingXS}px;
        border-radius: ${token.borderRadiusLG}px;
        font-size: ${token.fontSizeSM}px;
        font-weight: 600;
        border: 1px solid transparent;
      `,
      pillAcute: css`
        background: ${token.colorFillTertiary};
        color: ${token.colorTextSecondary};
      `,
      pillLab: css`
        background: ${token.colorFillTertiary};
        color: ${token.colorTextSecondary};
      `,
      kpis: css`
        display: flex;
        flex-wrap: wrap;
        gap: ${token.paddingXS}px;
      `,
      kpi: css`
        flex: 1 1 108px;
        background: ${token.colorFillQuaternary};
        border-radius: ${token.borderRadius}px;
        padding: ${token.paddingXS}px ${token.paddingSM}px;
      `,
      kpiLabel: css`
        line-height: 20px; /* = LgBadge height, so the LG label doesn't shift its value down */
        font-size: ${token.fontSizeSM - 1}px;
        color: ${token.colorTextTertiary};
        text-transform: uppercase;
        letter-spacing: 0.04em;
      `,
      kpiValue: css`
        font-size: ${token.fontSize}px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        margin-top: 2px;
      `,
      callout: css`
        background: ${token.colorWarningBg};
        border: 1px solid ${token.colorWarningBorder};
        border-radius: ${token.borderRadiusLG}px;
        padding: ${token.paddingSM}px ${token.paddingMD}px;
        font-size: ${token.fontSize}px;
        white-space: pre-wrap;
      `,
      calloutHead: css`
        display: flex;
        align-items: center;
        gap: 7px;
        font-weight: 600;
        color: ${token.colorWarningText};
        margin-bottom: 3px;
      `,
      cols: css`
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: start;
        gap: ${token.paddingMD}px ${token.paddingLG}px;
        @media (max-width: 640px) {
          grid-template-columns: 1fr;
        }
      `,
      // Fixed column: each section keeps its column so nothing reflows when a
      // sibling section is absent (e.g. Verrechnung must not jump left).
      col: css`
        display: flex;
        flex-direction: column;
        gap: ${token.paddingMD}px;
        min-width: 0;
      `,
      section: css`
        min-width: 0;
      `,
      eyebrow: css`
        font-size: ${token.fontSizeSM - 1}px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: ${token.colorTextTertiary};
        margin-bottom: ${token.paddingXS}px;
      `,
      text: css`
        font-size: ${token.fontSizeSM}px;
        line-height: ${token.lineHeightSM};
        color: ${token.colorTextSecondary};
        white-space: pre-wrap;
      `,
      tagRow: css`
        display: flex;
        flex-wrap: wrap;
        gap: ${token.paddingXXS}px;
      `,
      kv: css`
        display: grid;
        grid-template-columns: auto 1fr;
        gap: ${token.paddingXXS}px ${token.paddingMD}px;
        font-size: ${token.fontSize}px;
      `,
      kvKey: css`
        color: ${token.colorTextTertiary};
      `,
      inlineLink: css`
        height: auto;
        padding: 0;
        gap: ${token.paddingXXS}px;
      `,
      material: css`
        margin-top: ${token.paddingLG}px;
      `,
      materialGrid: css`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: ${token.paddingXS}px;
      `,
      specimenCard: css`
        border-radius: ${token.borderRadiusLG}px;
        padding: ${token.paddingSM}px;
        background: ${token.colorFillQuaternary};
      `,
      specimenName: css`
        font-size: ${token.fontSize}px;
        font-weight: 600;
      `,
      specimenSub: css`
        display: block;
        color: ${token.colorTextTertiary};
        font-size: ${token.fontSizeSM}px;
        margin-top: 1px;
      `,
      countBadge: css`
        display: inline-block;
        margin-left: ${token.paddingXXS}px;
        padding: 0 ${token.paddingXS}px;
        border: 1px solid ${token.colorPrimaryBorder};
        border-radius: ${token.borderRadiusLG}px;
        color: ${token.colorPrimary};
        font-size: ${token.fontSizeSM - 1}px;
        font-weight: 600;
        vertical-align: middle;
      `,
      localizations: css`
        margin-top: ${token.paddingXS}px;
      `,
      locTags: css`
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: ${token.paddingXXS}px;
      `,
      locTag: css`
        margin-inline-end: 0;
        font-size: ${token.fontSizeSM}px;
      `,
      locHint: css`
        color: ${token.colorTextTertiary};
        font-size: ${token.fontSizeSM}px;
      `,
      locFields: css`
        color: ${token.colorTextTertiary};
        font-size: ${token.fontSizeSM}px;
        margin-top: ${token.paddingXXS}px;
      `,
      tubeRows: css`
        display: flex;
        flex-direction: column;
        gap: ${token.paddingXS}px;
        margin-top: ${token.paddingXS}px;
      `,
      tubeRow: css`
        display: flex;
        gap: ${token.paddingXS}px;
        align-items: flex-start;
      `,
      tubeMeta: css`
        min-width: 0;
        font-size: ${token.fontSizeSM}px;
      `,
      tubeName: css`
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
      `,
      tubeLine: css`
        color: ${token.colorTextTertiary};
        margin-top: 1px;
      `,
      thumbRoot: css`
        flex: 0 0 auto;
        border-radius: ${token.borderRadius}px;
        overflow: hidden;
        background: #fff;
      `,
      thumb: css`
        object-fit: contain;
        cursor: zoom-in;
      `,
      thumbPlaceholder: css`
        flex: 0 0 40px;
        width: 40px;
        height: 40px;
      `,
      muted: css`
        color: ${token.colorTextSecondary};
      `,
    }),
    [token]
  );
};

export const ParameterInfoModal: React.FC<{
  parameter: ParameterSelection_RequestableParameterFragment | null;
  flipParamNames: boolean;
  showPrices: boolean;
  onClose: () => void;
}> = ({ parameter, flipParamNames, showPrices, onClose }) => {
  const { token } = useToken();
  const styles = useStyles();
  const auth = useAuth();
  const [imageUrls, setImageUrls] = useState<{ id: string; url: string }[]>([]);

  useEffect(() => {
    (async () => {
      setImageUrls([]);
      const result = [];
      for (const testTube of parameter?.specimens?.flatMap(it => it.testTubes) ?? []) {
        if (!testTube.hasImage) {
          continue;
        }
        const endpoint = `${window._env_.API_URL}/rest/test-tube/${testTube.id}/imageUrl`;
        const response = await fetch(endpoint, {
          headers: { authorization: `Bearer ${auth.user?.access_token}` },
        });
        const url = await response.text();
        result.push({ id: testTube.id, url: url ?? '' });
      }
      setImageUrls(result);
    })();
  }, [parameter, auth]);

  const patientPrice = parameter?.billingInfos.find(it => it.billingType === BillingType.PATIENT)?.price;
  const doctorPrice = parameter?.billingInfos.find(it => it.billingType === BillingType.DOCTOR)?.price;
  const lgDoctorPrice = parameter?.billingInfos.find(it => it.billingType === BillingType.LG_DOCTOR)?.price;
  const insurancePrice = parameter?.billingInfos.find(
    it => it.billingType === BillingType.INSURANCE || it.billingType === BillingType.DIAGNOSE
  )?.price;
  const lgInsurancePrice = parameter?.billingInfos.find(
    it => it.billingType === BillingType.LG_INSURANCE || it.billingType === BillingType.LG_DIAGNOSE
  )?.price;

  const specialRates = parameter?.billingInfos.filter(it => it.billingType === BillingType.SPECIAL_RATE) ?? [];
  const specimens = useMemo(
    () => [...(parameter?.specimens ?? [])].sort((a, b) => deCollator.compare(a.name, b.name)),
    [parameter?.specimens]
  );

  const lgTag = <LgBadge />;
  const kpis: { key: string; label: React.ReactNode; value: string }[] = [];
  if (showPrices) {
    if (patientPrice !== null && patientPrice !== undefined) {
      kpis.push({ key: 'patient', label: 'Patient', value: currency(patientPrice) });
    }
    if (doctorPrice !== null && doctorPrice !== undefined) {
      kpis.push({ key: 'doctor', label: 'Zuweiser', value: currency(doctorPrice) });
    }
    if (lgDoctorPrice !== null && lgDoctorPrice !== undefined) {
      kpis.push({ key: 'lgDoctor', label: <>{lgTag} Zuweiser</>, value: currency(lgDoctorPrice) });
    }
    if (insurancePrice !== null && insurancePrice !== undefined) {
      kpis.push({ key: 'insurance', label: 'SB Kasse', value: currency(insurancePrice) });
    }
    if (lgInsurancePrice !== null && lgInsurancePrice !== undefined) {
      kpis.push({ key: 'lgInsurance', label: <>SB {lgTag} Kasse</>, value: currency(lgInsurancePrice) });
    }
    specialRates.forEach((rate, index) => {
      if (rate.price !== null && rate.price !== undefined) {
        kpis.push({ key: `special-${index}`, label: rate.text, value: currency(rate.price) });
      }
    });
  }
  if (parameter?.evalHours && parameter.evalHours !== -1) {
    kpis.push({ key: 'evalHours', label: 'Auswertung', value: humanizedHoursShort(parameter.evalHours) });
  }
  if (parameter?.volume) {
    kpis.push({ key: 'volume', label: 'Volumen', value: String(parameter.volume) });
  }

  const billingLabels = billingTypeTagLabelsFull(parameter?.billingInfos.map(bi => bi.billingType) ?? []);
  const hasCompat = !!parameter?.withParameterNames.length || !!parameter?.withoutParameterNames.length;
  const hasVerrechnung = billingLabels.length > 0 || !!parameter?.billingDescription;

  return (
    <Modal
      title={
        <div className={styles.header}>
          {parameter && (
            <span>
              <ParamNameTitle
                shortName={parameter.shortName}
                longName={parameter.longName}
                flipParamNames={flipParamNames}
                classNames={{ primary: styles.name, secondary: styles.nameSecondary }}
              />
            </span>
          )}
          {!!parameter?.acute && (
            <span className={cx(styles.pill, styles.pillAcute)}>
              <ClockCircleOutlined /> Akut
            </span>
          )}
          {parameter?.lab && <span className={cx(styles.pill, styles.pillLab)}>{parameter.lab.shortName}</span>}
        </div>
      }
      okText="Schließen"
      okButtonProps={{ icon: <CloseOutlined />, type: 'default' }}
      open={parameter !== null}
      onOk={onClose}
      onCancel={onClose}
      cancelButtonProps={{ hidden: true }}
      width={860}
      centered
      styles={{
        header: {
          marginBottom: token.margin,
          paddingBottom: token.paddingSM,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        },
        body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' },
      }}
      destroyOnHidden
    >
      {!!kpis.length && (
        <div className={styles.kpis}>
          {kpis.map(kpi => (
            <div key={kpi.key} className={styles.kpi}>
              <div className={styles.kpiLabel}>{kpi.label}</div>
              <div className={styles.kpiValue}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {!!parameter?.importantDescription && (
        <div className={styles.callout} style={{ marginTop: token.padding }}>
          <div className={styles.calloutHead}>
            <WarningFilled /> Wichtige Informationen
          </div>
          {parameter.importantDescription}
        </div>
      )}

      <div className={styles.cols} style={{ marginTop: token.padding }}>
        <div className={styles.col}>
          {hasVerrechnung && (
            <div className={styles.section}>
              <div className={styles.eyebrow}>Verrechnung</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: token.paddingXS }}>
                {billingLabels.length > 0 && (
                  <div className={styles.tagRow}>
                    {billingLabels.map(label => (
                      <Tag key={label} icon={<CheckOutlined className={css`color: ${token['green-7']}`} />}>
                        {label}
                      </Tag>
                    ))}
                  </div>
                )}
                {!!parameter?.billingDescription && <div className={styles.text}>{parameter.billingDescription}</div>}
              </div>
            </div>
          )}

          {hasCompat && (
            <div className={styles.section}>
              <div className={styles.eyebrow}>Kompatibilität</div>
              <dl className={styles.kv}>
                {!!parameter?.withParameterNames.length && (
                  <>
                    <dt className={styles.kvKey}>Nur mit</dt>
                    <dd style={{ margin: 0 }}>
                      <div className={styles.tagRow}>
                        {parameter.withParameterNames.map(it => (
                          <Tag key={it} className={monoFont}>
                            {it}
                          </Tag>
                        ))}
                      </div>
                    </dd>
                  </>
                )}
                {!!parameter?.withoutParameterNames.length && (
                  <>
                    <dt className={styles.kvKey}>Nicht mit</dt>
                    <dd style={{ margin: 0 }}>
                      <div className={styles.tagRow}>
                        {parameter.withoutParameterNames.map(it => (
                          <Tag key={it} className={monoFont}>
                            {it}
                          </Tag>
                        ))}
                      </div>
                    </dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </div>

        <div className={styles.col}>
          {!!parameter?.description && (
            <div className={styles.section}>
              <div className={styles.eyebrow}>Informationen</div>
              <div className={styles.text}>{parameter.description}</div>
            </div>
          )}

          {!!parameter?.synonyms?.length && (
            <div className={styles.section}>
              <div className={styles.eyebrow}>Synonyme</div>
              <div className={styles.tagRow}>
                {parameter.synonyms.map(it => (
                  <Tag key={it}>{it}</Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {!!parameter?.link && (
        <Button
          type="primary"
          style={{ marginTop: token.padding }}
          icon={<InfoCircleOutlined />}
          onClick={() => openExternalLink(parameter.link)}
        >
          Weitere Informationen
        </Button>
      )}

      <div className={styles.material}>
        <div className={styles.eyebrow} style={{ marginBottom: token.padding }}>
          Material &amp; Probenbehälter
        </div>
        {!specimens.length && <div className={styles.muted}>Für diesen Parameter wird kein Material benötigt</div>}
        <div className={styles.materialGrid}>
          {specimens.map(specimen => {
            const tubeCount = specimen.testTubes.length;
            return (
              <div key={specimen.id} className={styles.specimenCard}>
                <div>
                  <span className={styles.specimenName}>{specimen.name}</span>
                  {tubeCount > 1 && <span className={styles.countBadge}>{tubeCount} Behälter</span>}
                  <span className={styles.specimenSub}>
                    {specimen.classification ? `Materialkennung ${specimen.classification} · ` : ''}
                    Lagerdauer {humanizedHoursShort(specimen.storagePeriod)}
                    {specimen.requiresLocalizations && ' · Lokalisation erforderlich'}
                  </span>
                </div>
                {specimen.requiresLocalizations && (
                  <div className={styles.localizations}>
                    {specimen.predefinedLocalizations.length ? (
                      <div className={styles.locTags}>
                        {/* rendered in stored order - that is the configured anatomical order, never sort it */}
                        {specimen.predefinedLocalizations.map(location => (
                          <Tag key={location} className={styles.locTag}>
                            {location}
                          </Tag>
                        ))}
                        {specimen.localizationInputEnabled && <span className={styles.locHint}>+ Freitext möglich</span>}
                      </div>
                    ) : (
                      <div className={styles.locHint}>
                        {specimen.localizationInputEnabled ? 'Nur Freitext' : 'Keine Vorgaben hinterlegt'}
                      </div>
                    )}
                    {specimen.localizationFields.length > 0 && (
                      <div className={styles.locFields}>
                        Zusatzfelder: {specimen.localizationFields.map(describeLocalizationField).join(', ')}
                      </div>
                    )}
                  </div>
                )}
                {tubeCount === 0 ? (
                  <div className={styles.muted} style={{ marginTop: token.paddingXS }}>
                    Keine Probenbehälter benötigt
                  </div>
                ) : (
                  <div className={styles.tubeRows}>
                    {[...specimen.testTubes]
                      .sort((a, b) => deCollator.compare(a.name, b.name))
                      .map(testTube => {
                        const url = imageUrls.find(iu => iu.id === testTube.id)?.url;
                        // Reserve the thumbnail slot on every row once any tube in
                        // the specimen has an image, so the texts stay aligned.
                        const specimenHasImages = specimen.testTubes.some(tt => imageUrls.some(iu => iu.id === tt.id));
                        const tubeColor = testTubeColorProperties(testTube.color);
                        const swatch = <TestTubeColor withText={false} properties={tubeColor} />;
                        // With multiple tubes always label each, so it never
                        // reads as a mix of named and unnamed rows.
                        const showName = specimen.testTubes.length > 1 || testTube.name !== specimen.name;
                        return (
                          <div key={testTube.id} className={styles.tubeRow}>
                            {url ? (
                              <Image
                                rootClassName={styles.thumbRoot}
                                className={styles.thumb}
                                src={url}
                                width={40}
                                height={40}
                                preview={{ mask: <ZoomInOutlined /> }}
                              />
                            ) : (
                              specimenHasImages && <div className={styles.thumbPlaceholder} />
                            )}
                            <div className={styles.tubeMeta}>
                              {showName && <div className={styles.tubeName}>{testTube.name}</div>}
                              <div className={styles.tubeLine}>
                                {swatch}
                                {` ${tubeColor.translation} · `}
                                Etikett {testTube.labelText}
                                {testTube.labelText2 ? ` / ${testTube.labelText2}` : ''}
                                {testTube.specialPrinterRequired ? ' · Spezial-Drucker' : ''}
                                {testTube.volume ? ` · Volumen ${testTube.volume}` : ''}
                              </div>
                              {!!testTube.link && (
                                <Button
                                  type="link"
                                  className={styles.inlineLink}
                                  style={{ fontSize: token.fontSizeSM }}
                                  icon={<ShoppingCartOutlined />}
                                  onClick={() => openExternalLink(testTube.link)}
                                >
                                  Online bestellen
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
